import postsData from '@/data/posts.json'

export interface PostData {
  slug: string
  title: string
  description: string
  summary: string
  date: string
  tags: string[]
  categories?: string[]
  contentHtml: string
  id?: string
  excerpt?: string
}

function isValidPost(value: unknown): value is PostData {
  if (typeof value !== 'object' || value === null) return false
  const post = value as Record<string, unknown>
  return (
    typeof post.slug === 'string' &&
    typeof post.title === 'string' &&
    typeof post.date === 'string' &&
    Array.isArray(post.tags) &&
    typeof post.contentHtml === 'string'
  )
}

// Validate at the boundary: keep only well-formed records so malformed
// JSON entries cannot crash downstream consumers. Falls back to [].
const allPosts: PostData[] = Array.isArray(postsData)
  ? (postsData as unknown[]).filter(isValidPost)
  : []

export function getAllPosts(): PostData[] {
  return getSortedPostsData()
}

export function getSortedPostsData(): PostData[] {
  return [...allPosts].sort((a, b) => {
    if (a.date < b.date) return 1
    return -1
  })
}

export function getAllPostSlugs() {
  return allPosts.map((post) => ({
    params: { slug: post.slug }
  }))
}

export async function getPostData(slug: string): Promise<PostData> {
  const post = allPosts.find(p => p.slug === slug)
  if (!post) {
    throw new Error(`Post not found: ${slug}`)
  }
  return post
}

export function getPostsByTag(tag: string): PostData[] {
  return getSortedPostsData().filter(post => post.tags.includes(tag))
}

export function getAllTags(): string[] {
  const tags = new Set<string>()
  allPosts.forEach(post => {
    post.tags.forEach(tag => tags.add(tag))
  })
  return Array.from(tags).sort()
}
